import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorScreen, LoadingScreen } from '@/components/status-screen';
import { loginErrorKey } from '@/features/auth/error-message';
import { SESSION_KEY, sessionQuery } from '@/features/auth/session-query';
import { useSession } from '@/lib/auth/session-context';
import type { createAppRouter } from '@/routes/router';

/**
 * Restores the session from the refresh cookie before any route renders, so
 * route guards can decide synchronously and the first paint is a loading
 * state, never a blank page (PRD NFR-U5).
 */
export function App({ router }: { router: ReturnType<typeof createAppRouter> }) {
  const { t } = useTranslation(['common', 'auth']);
  const session = useSession();
  const queryClient = useQueryClient();
  const bootstrap = useQuery(sessionQuery(session));

  // A background refresh that finds the session over signs the UI out.
  useEffect(
    () => session.subscribe((user) => queryClient.setQueryData(SESSION_KEY, user)),
    [session, queryClient],
  );

  if (bootstrap.isPending) return <LoadingScreen label={t('common:loading.session')} />;
  if (bootstrap.isError) {
    return (
      <ErrorScreen
        message={t(loginErrorKey(bootstrap.error))}
        onRetry={() => bootstrap.refetch()}
      />
    );
  }
  return <RouterProvider router={router} />;
}
